import React from 'react';

const ProfileDetails = ({ scoreAllList, createdAt,userName }) => {

  const hacksDeepThinker = [
    {
      id: 'Hack 1',
      title: 'BLOCK THE DEEP DIVE:',
      description: 'Reserve a 90-120 minute uninterrupted block daily for your hardest problem, before any meetings or email - your single most reliable on-ramp to flow.',
    },
    {
      id: 'Hack 2',
      title: 'GO DARK ON DIGITAL:',
      description: 'Turn off all notifications, close every unrelated tab, and put your phone in another room during deep work. Treat interruption as the enemy of your flow, not a minor cost.',
    },
    {
      id: 'Hack 3',
      title: 'RAISE THE CHALLENGE:',
      description: 'When work feels too easy, deliberately add complexity or a constraint. Your flow lives at the upper edge of difficulty, where mastery is genuinely tested.',
    },
  ];

  const hacksHardCharger = [
    {
      id: 'Hack 1',
      title: 'ENGINEER STAKES',
      description: 'Manufacture real consequences - a public commitment, a competitive deadline, a bet - so even routine work carries the challenge that triggers your flow.',
    },
    {
      id: 'Hack 2',
      title: 'FRONT-LOAD THE HARD STUFF',
      description: 'Tackle your most challenging, high-consequence task while your energy is peak. You flow into difficulty, not away from it.',
    },
    {
      id: 'Hack 3',
      title: 'BUILD IN RECOVERY',
      description: 'Schedule deliberate downtime after intense pushes. Your flow depends on oscillation - without recovery, intensity curdles into burnout.',
    }
  ];

  const hacksFlowGoer = [
    {
      id: 'Hack 1',
      title: 'CHASE NOVELTY ON PURPOSE',
      description: 'Change your environment, tools, or approach when energy dips. A new setting or a fresh angle is your fastest route back into flow.',
    },
    {
      id: 'Hack 2',
      title: 'PLAY YOUR WAY IN',
      description: 'Start hard tasks with a low-pressure, exploratory play phase before committing to structure. Curiosity opens the door that obligation closes.',
    },
    {
      id: 'Hack 3',
      title: 'ANCHOR ONE COMPLETION RITUAL',
      description: 'Pair your love of starting with one disciplined finish-it habit - e.g. a daily ship one thing rule - so novelty doesnt cost you follow-through.',
    },
  ];

  const hacksCrowdPleaser = [
    {
      id: 'Hack 1',
      title: 'DESIGN FOR GROUP FLOW',
      description: 'Schedule real-time collaboration - co-working, live sessions, pair work - for the tasks that matter most. Shared presence is your strongest trigger.',
    },
    {
      id: 'Hack 2',
      title: 'TURN SOLO WORK SOCIAL',
      description: 'When you must work alone, add a social layer - body-doubling, a co-working call, or an accountability partner - to import the energy you need.',
    },
    {
      id: 'Hack 3',
      title: 'PROTECT LIVE TIME REMOTELY',
      description: 'In remote/async settings, deliberately defend synchronous, camera-on time. Dont let everything drift to silent async, which starves your flow.',
    },
  ];

  let getStatus = (list, name, fieldName) => {
    const item = list.find(ele => ele.name === name);
    return item ? item[fieldName] : null;
  };

  const flowProfiles = [
    {
      name: "Deep Thinker",
      color: "#0284C7",
      bg: "bg-sky-50/40",
      shortDesc:
        "The solitary analyst who finds flow in deep, uninterrupted focus.",
      howYouFlow:
        "You enter flow by going deep and alone. When the door is closed, the problem is hard enough to demand all of you, and the world goes quiet, your attention locks on and time dissolves. Flow for you is the intense, absorbed concentration of a mind fully engaged with a worthy problem - quiet, internal, and profound.",
      blockers:
        "Your flow-blockers are interruption, over-analysis, and isolation. Constant context-switching (chat, meetings, notifications) is uniquely damaging to your deep concentration. Watch the flip side too: too much solitude can tip into perfectionism, rumination, and analysis paralysis. Schedule deliberate input - a peer review, a hard deadline - to break you out of endless polishing.",
      hacks: hacksDeepThinker
    },

    {
      name: "Hard Charger",
      color: "#A84B2F",
      bg: "bg-[#A84B2F]/5",
      shortDesc:
        "The bold risk-taker who finds flow on the edge of high consequence.",
      howYouFlow:
        "You enter flow on the edge - where the stakes are real, the pace is fast, and the outcome is uncertain. Pressure that paralyzes others switches you on. Flow for you is the intense, embodied focus of decisive action under consequence: fully committed, fully alive, all hesitation gone. You feel most yourself when you are pushing your limits.",
      blockers:
        "Your flow-blockers are burnout, recklessness, and low-stimulation environments. You chase intensity past sustainability and can take risks without building in recovery or reflection. Quiet, low-stakes remote work can leave you restless and disengaged - if your environment lacks visible challenge, create your own targets and stakes, and guard against using adrenaline as a substitute for rest.",
      hacks: hacksHardCharger
    },

    {
      name: "Flow Goer",
      color: "#20808D",
      bg: "bg-[#20808D]/5",
      shortDesc:
        "The open, adaptable explorer who finds flow through novelty and ease.",
      howYouFlow:
        "You enter flow through novelty, play, and ease. When you are free to explore, follow curiosity, and improvise, the state arrives almost effortlessly. Flow for you is open and adaptive - riding the current rather than forcing it, connecting ideas others miss, and finding delight in the new. Variety is not a distraction for you; it is fuel.",
      blockers:
        "Your flow-blockers are scattered focus and weak follow-through. The same openness that fuels your flow can splinter it - too many interests, too many unfinished projects, too much digital novelty (endless tabs, feeds, and shiny new tools) fragmenting your attention. Rigid structure also blocks you, so don't over-correct; instead add just enough lightweight discipline to convert exploration into finished work.",
      hacks: hacksFlowGoer
    },

    {
      name: "Crowd Pleaser",
      color: "#7A39BB",
      bg: "bg-[#7A39BB]/5",
      shortDesc:
        "The connected collaborator who finds flow in shared, social momentum.",
      howYouFlow:
        "You enter flow with and through other people. In a humming team, a live audience, or a real-time collaboration, you light up and so does the group. Flow for you is shared - 'group flow' - where energy, ideas, and momentum pass between people and lift everyone. You read the room, build on others, and feel most alive in connection.",
      blockers:
        "Your flow-blockers are isolation, dependence on others, and people-pleasing. Prolonged solitude and silent async work drain you and make it hard to self-start. Watch the tendency to prioritize harmony over hard truths or to lose your own focus in the group's energy. Build a small amount of solo-focus capacity so you are not fully dependent on others to drop into flow.",
      hacks: hacksCrowdPleaser
    }
  ];


  return (
    <>
      <>
        {flowProfiles.map((profile, pageIndex) => (
          <div key={pageIndex} className="p-10 px-6 space-y-6 bg-white">
            <div className="px-12 space-y-6 page-break">

              {/* Header */}
              <div className="flex items-center justify-between w-full pt-4 pb-2 border-b text-[#212121]/85 italic">
                <h1 className="text-lg font-semibold">  Flow Profile Report  </h1>
                <p className="text-lg font-semibold">{userName} | {createdAt}</p>
              </div>

              <div className="mt-6 mb-4">
                <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Result Reports</h1>
                <p className="text-base text-gray-600 mt-1">How you flow · three Flow Hacks · your specific flow-blockers - per profile.
                  <br /> Read your PRIMARY profile first, then your SECONDARY. Your least-used profile shows where to grow your range.</p>
              </div>

              {/* Profile Banner */}
              <div
                className={`${profile.bg} border border-gray-100 border-l-4 p-6 rounded-r-md mt-6 mb-8`}
                style={{ borderLeftColor: profile.color }}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <span
                      className="block text-sm uppercase font-bold tracking-wider"
                      style={{ color: profile.color }}
                    >
                      FLOW PROFILE
                    </span>

                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                      {profile.name}
                    </h1>
                  </div>

                  <span className={'text-base font-bold px-2.5 py-1 w-32 text-center rounded border tracking-wider ' + getStatus(scoreAllList, profile.name, "colorClass")} >
                    {getStatus(scoreAllList, profile.name, "status")}
                  </span>
                </div>

                <p
                  className="font-medium text-base mt-2"
                  style={{ color: profile.color }}
                >
                  {profile.shortDesc}
                </p>

                <div className="mt-5 pt-4">
                  <h4 className="text-lg font-bold uppercase mb-2">
                    HOW YOU FLOW
                  </h4>

                  <p className="text-base text-gray-700 leading-relaxed">
                    {profile.howYouFlow}
                  </p>
                </div>
              </div>

              {/* Hacks */}
              <div className="space-y-4 mb-8">
                <div className='flex items-center gap-2 text-lg pt-2'>
                  <h2 className='text-gray-900 font-bold'>Flow Hacks:</h2>
                  <p className='text-gray-700'>Three practical steps to drop into flow faster</p>
                </div>

                {profile.hacks.map((hack, index) => (
                  <div
                    key={index}
                    className="flex border border-gray-300 rounded-xl p-4 gap-4"
                  >
                    <div
                      className="px-3 flex items-center justify-center font-bold text-white"
                      style={{ backgroundColor: `${profile.color}` }}
                    >
                      Hack {index + 1}
                    </div>

                    <div className="flex-1">
                      <h3 className="font-bold uppercase mb-1">
                        {hack.title}
                      </h3>

                      <p className="text-sm text-gray-600">
                        {hack.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Blockers */}
              <div
                className={`${profile.bg} border rounded-xl p-5`}
                style={{ borderColor: profile.color }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="w-3 h-3 rounded-sm"
                    style={{ backgroundColor: profile.color }}
                  />

                  <h3 className="font-bold uppercase">
                    FLOW-BLOCKERS TO WATCH
                  </h3>
                </div>

                <p className="text-gray-700 leading-relaxed">
                  {profile.blockers}
                </p>
              </div>

            </div>
          </div>
        ))}
      </>
    </>
  );
};

export default ProfileDetails;